import {useMemo, useState} from 'react'
import {X} from 'lucide-react'
import {useQuery} from '@tanstack/react-query'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useServices} from '@/services/context'
import type {LookupDescriptor} from '@/types/endpoint-contract'
import type {EntityLink, EntityType} from '@/types/entity-link'
import {EntityType as EntityTypeValues} from '@/types/entity-link'
import type {QueryParams, Services} from '@/services/types'
import type {Page} from '@/types/page'

const RESULT_LIMIT = 5
// WHY: legacy LookupDescriptor.labelField is optional during the 525 transition;
// when omitted we read from the EntityLink projection's `displayName` slot.
const DEFAULT_LABEL_FIELD = 'displayName'

/**
 * Resolve the {@link LookupDescriptor.endpoint} (an `ApiEndpoint` enum entry
 * name like `'apiInternalRoles'`) to the matching key on the {@link Services}
 * registry — strip the `apiInternal` prefix and lowercase the first letter.
 *
 * The mapping mirrors the existing project convention: `apiInternalRoles`
 * maps to `services.roles`, `apiInternalAccessProfiles` to
 * `services.accessProfiles`, and so on. Throws when the resulting key is
 * not registered on the services object — surfaces lookup-misconfiguration
 * loudly per the project's "errors must be loud" rule.
 *
 * After Phase 525 only `list()` is required — the EntityLink-projection
 * endpoints carry the displayName on every row, so no separate `detail()`
 * call is ever needed.
 */
function resolveServiceForEndpoint(services: Services, endpoint: string): {
    list: (params?: QueryParams) => Promise<Page<LookupRecord> | LookupRecord[]>
} {
    const PREFIX = 'apiInternal'
    if (!endpoint.startsWith(PREFIX)) {
        throw new Error(
            `LookupCombobox: endpoint '${endpoint}' does not match the apiInternalX convention — `
            + `cannot resolve to a services key`,
        )
    }
    const stripped = endpoint.slice(PREFIX.length)
    const key = stripped.charAt(0).toLowerCase() + stripped.slice(1)
    const candidate = (services as unknown as Record<string, unknown>)[key]
    if (!candidate || typeof candidate !== 'object') {
        throw new Error(
            `LookupCombobox: services.${key} is not registered — `
            + `derived from endpoint '${endpoint}'`,
        )
    }
    const svc = candidate as {
        list?: (p?: QueryParams) => Promise<Page<LookupRecord> | LookupRecord[]>
    }
    if (typeof svc.list !== 'function') {
        throw new Error(
            `LookupCombobox: services.${key} must expose list() — `
            + `derived from endpoint '${endpoint}'`,
        )
    }
    return svc as ReturnType<typeof resolveServiceForEndpoint>
}

interface LookupRecord {
    readonly id: string
    readonly [key: string]: unknown
}

function readField(record: LookupRecord, field: string): string {
    const raw = record[field]
    if (raw == null) return ''
    if (typeof raw === 'string') return raw
    if (typeof raw === 'object' && raw !== null && 'en' in (raw as Record<string, unknown>)) {
        const localized = (raw as Record<string, unknown>).en
        return typeof localized === 'string' ? localized : String(localized ?? '')
    }
    return String(raw)
}

function unwrapList(payload: Page<LookupRecord> | LookupRecord[]): LookupRecord[] {
    if (Array.isArray(payload)) return payload
    return payload.content ?? []
}

const KNOWN_ENTITY_TYPES: ReadonlySet<string> = new Set(Object.values(EntityTypeValues))

function resolveEntityType(descriptor: LookupDescriptor): EntityType {
    if (descriptor.entityType && KNOWN_ENTITY_TYPES.has(descriptor.entityType)) {
        return descriptor.entityType
    }
    // WHY: legacy 429 contracts predate `entityType`. Default to IDENTITY so
    // the value still composes; Phase 10 of issue 525 retroactively sets the
    // correct entityType on every contract.
    return EntityTypeValues.IDENTITY
}

/**
 * Project a row returned by `service.list({q})` into an {@link EntityLink}.
 * For new {@code /links} endpoints the row is already an EntityLink shape and
 * passes through; for legacy endpoints it derives `displayName` from the
 * descriptor-named field and the `entityType` from the descriptor.
 */
function rowToEntityLink(record: LookupRecord, descriptor: LookupDescriptor): EntityLink {
    const labelField = descriptor.labelField ?? DEFAULT_LABEL_FIELD
    const displayName = readField(record, labelField)
    const recordEntityType = typeof record.entityType === 'string' && KNOWN_ENTITY_TYPES.has(record.entityType)
        ? (record.entityType as EntityType)
        : resolveEntityType(descriptor)
    const recordSubType = typeof record.subType === 'string' ? record.subType : null
    return {id: record.id, displayName, entityType: recordEntityType, subType: recordSubType, code: null}
}

interface SingleProps {
    readonly mode: 'single'
    readonly value: EntityLink | null
    readonly onChange: (next: EntityLink | null) => void
}

interface MultiProps {
    readonly mode: 'multi'
    readonly values: ReadonlyArray<EntityLink>
    readonly onChange: (next: EntityLink[]) => void
}

interface CommonProps {
    readonly lookup: LookupDescriptor
    readonly displayName: string
    readonly testIdPrefix: string
}

export type LookupComboboxProps = CommonProps & (SingleProps | MultiProps)

/**
 * Generic entity-reference picker driven by a {@link LookupDescriptor}. Handles
 * both single-select (`kind: 'uuid'`) and multi-select (`kind: 'csv', element:
 * 'uuid'`) modes — the multi mode adds a removable chip-list above the input.
 *
 * On open the picker fires `{size: 5}` with no `q`, so the user sees the top
 * five candidates immediately and can recognise without typing. Every keystroke
 * after that re-runs the search with `{[lookup.searchParam]: text, size: 5}`.
 * Each row is projected into an {@link EntityLink} (`{id, displayName,
 * entityType, subType}`); the chip and selected-label render directly from
 * the value's `displayName` slot — no separate {@code service.detail()} call
 * needed.
 *
 * Service resolution: `lookup.endpoint` (e.g., `'apiInternalIdentitiesLinks'`)
 * maps to `services.identitiesLinks` via the standard `apiInternalX → services.x`
 * convention. Resolution is loud — a mistyped endpoint throws with a clear
 * error rather than silently degrading.
 */
export function LookupCombobox(props: LookupComboboxProps) {
    const {lookup, displayName, testIdPrefix} = props
    const services = useServices()
    const [searchQuery, setSearchQuery] = useState('')

    const service = useMemo(
        () => resolveServiceForEndpoint(services, lookup.endpoint),
        [services, lookup.endpoint],
    )

    // WHY: empty-q dropdown shows the top 5 rows the moment the picker opens.
    // Server-side ordering by displayName lives inside each module's findLinks
    // service — the frontend doesn't send a sort param so it doesn't have to
    // know that some entities use `displayName` and others use `name`.
    const searchResult = useQuery({
        queryKey: ['lookup', lookup.endpoint, lookup.searchParam, searchQuery],
        queryFn: async () => {
            const params: QueryParams = {size: RESULT_LIMIT}
            if (searchQuery.length > 0) {
                params[lookup.searchParam] = searchQuery
            }
            const payload = await service.list(params)
            return unwrapList(payload).map((row) => rowToEntityLink(row, lookup))
        },
    })

    const options: ComboboxOption[] = useMemo(() => {
        const records = searchResult.data ?? []
        return records.map((link) => ({
            value: link.id,
            label: link.displayName,
        }))
    }, [searchResult.data])

    const findInSearchResults = (id: string): EntityLink | undefined =>
        (searchResult.data ?? []).find((link) => link.id === id)

    if (props.mode === 'multi') {
        const picked = props.values
        const handlePick = (id: string | null) => {
            if (id == null) return
            if (picked.some((link) => link.id === id)) {
                setSearchQuery('')
                return
            }
            const link = findInSearchResults(id)
            if (link == null) return
            props.onChange([...picked, link])
            setSearchQuery('')
        }
        const handleRemove = (id: string) => {
            props.onChange(picked.filter((link) => link.id !== id))
        }
        return (
            <div className="space-y-2" data-testid={`${testIdPrefix}-lookup-multi`}>
                {picked.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-chips`}>
                        {picked.map((link) => (
                            <button
                                key={link.id}
                                type="button"
                                onClick={() => handleRemove(link.id)}
                                data-testid={`${testIdPrefix}-chip-${link.id}`}
                                aria-label={`Remove ${link.displayName}`}
                                className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-0.5 text-sm hover:bg-primary/20"
                            >
                                <span className="truncate max-w-[12rem]">
                                    {link.displayName}
                                </span>
                                <X className="h-3 w-3"/>
                            </button>
                        ))}
                    </div>
                )}
                <Combobox
                    options={options}
                    value={null}
                    onValueChange={handlePick}
                    onSearch={setSearchQuery}
                    isLoading={searchResult.isLoading}
                    placeholder={displayName}
                    data-testid={`${testIdPrefix}-combobox`}
                />
                {searchResult.isError && (
                    <div data-testid={`${testIdPrefix}-error`}>
                        <ErrorBanner error={searchResult.error as Error}/>
                    </div>
                )}
            </div>
        )
    }

    const singleValue = props.value
    const handleSinglePick = (id: string | null) => {
        if (id == null) {
            props.onChange(null)
            return
        }
        const link = findInSearchResults(id)
        if (link == null) return
        props.onChange(link)
    }

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-lookup-single`}>
            <Combobox
                options={options}
                value={singleValue ? singleValue.id : null}
                onValueChange={handleSinglePick}
                onSearch={setSearchQuery}
                isLoading={searchResult.isLoading}
                selectedLabel={singleValue ? singleValue.displayName : undefined}
                placeholder={displayName}
                data-testid={`${testIdPrefix}-combobox`}
            />
            {searchResult.isError && (
                <div data-testid={`${testIdPrefix}-error`}>
                    <ErrorBanner error={searchResult.error as Error}/>
                </div>
            )}
        </div>
    )
}
